/**
 * Catalog quyền phía Admin FE (role 'mod' / CTV).
 *
 * Nguồn quyền thật nằm ở backend (configs/admin-permissions.js). File này map
 * route path <-> permKey để lọc sidebar + guard route + render UI phân quyền.
 * Giữ key TRÙNG với backend.
 */

export type UserInfo = { role?: string; permissions?: Record<string, boolean> } | undefined | null;

/** segment đầu của path -> permKey. Path không có trong map => admin-only. */
export const PATH_PERM: Record<string, string> = {
    dashboard: "dashboard",
    // Dùng CHUNG quyền 'dashboard' — backend cũng bảo vệ /analytics/* bằng
    // authorize('dashboard'), thêm permKey riêng ở FE sẽ lệch với backend.
    "app-growth": "dashboard",
    user: "users",
    topuser: "users",
    order: "orders",
    commission: "commission",
    "app-rate-promo": "campaign_x2",
    "reward-program": "campaign_x2", // nền tảng mới dùng chung quyền với Chiến dịch X2 cũ
    "xu-program": "xu_program",
    payment: "finance",
    withdraw: "finance",
    bonus: "finance",
    email: "email",
    push: "push",
    voucher: "voucher",
    banner: "banner",
    referral: "referral",
    "referred-users": "referral",
    post: "post",
    comment: "comment",
    community: "community",
    videolist: "video",
    "utility-block": "utility",
    "guide-video": "guide_video",
    "tiktok-affiliate-config": "tiktok_shopping",
    "product-notes": "tiktok_shopping",
    "app-version-gate": "app_version",
    logs: "logs",
};

/** Nhóm quyền cho UI phân quyền (label VN). Khớp cấu trúc sidebar. */
export const PERMISSION_GROUPS: { title: string; items: { key: string; label: string }[] }[] = [
    { title: "Tổng quan", items: [{ key: "dashboard", label: "Tổng quan (Dashboard)" }] },
    { title: "Người dùng", items: [{ key: "users", label: "Users / Top Users" }] },
    {
        title: "Đơn hàng",
        items: [
            { key: "orders", label: "Đơn Hàng" },
            { key: "commission", label: "Set Hoa Hồng" },
            { key: "campaign_x2", label: "Chương trình thưởng (+ X2 cũ)" },
            { key: "xu_program", label: "Xu: Điểm danh / Nhiệm vụ / Hạng" },
        ],
    },
    { title: "Tài chính", items: [{ key: "finance", label: "Thanh toán / Rút tiền / Thưởng" }] },
    {
        title: "Marketing",
        items: [
            { key: "email", label: "Email Campaign" },
            { key: "push", label: "Push Notification" },
            { key: "voucher", label: "Voucher" },
            { key: "banner", label: "Banner" },
            { key: "referral", label: "Giới thiệu / Được giới thiệu" },
        ],
    },
    {
        title: "Nội dung",
        items: [
            { key: "post", label: "Bài viết Blog" },
            { key: "comment", label: "Bình luận" },
            { key: "community", label: "Kênh cộng đồng" },
            { key: "video", label: "Quản lý Video" },
            { key: "utility", label: "Tiện ích App" },
            { key: "guide_video", label: "Video hướng dẫn" },
            { key: "tiktok_shopping", label: "Mua sắm TikTok" },
        ],
    },
    {
        title: "Hệ thống",
        items: [
            { key: "app_version", label: "Nhắc cập nhật App" },
            { key: "logs", label: "Logs & Lỗi" },
        ],
    },
];

/** Quyền đặc biệt (không phải mục menu). */
export const FLAG_PERMISSIONS: { key: string; label: string }[] = [
    { key: "notify_post_comments", label: "Nhận thông báo bình luận mọi bài viết (giống admin)" },
    { key: "notify_utility_requests", label: "Nhận thông báo tiện ích có yêu cầu mới chờ xử lý" },
];

/** Preset nhanh cho CTV cơ bản. */
export const CTV_BASIC_PRESET: Record<string, boolean> = {
    comment: true,
    referral: true,
    logs: true,
    notify_post_comments: true,
};

const isAdmin = (u: UserInfo) => u?.role === "admin";

/**
 * permissions có thể là object HOẶC chuỗi JSON (cột trên prod là TEXT, không phải JSON —
 * schema đi qua migration tay nên kiểu cột không khớp khai báo model)
 * -> luôn trả về object { <permKey>: true }.
 */
export function normalizePerms(raw: unknown): Record<string, boolean> {
    if (!raw) return {};
    if (typeof raw === "string") {
        try {
            return JSON.parse(raw) || {};
        } catch {
            return {};
        }
    }
    return raw as Record<string, boolean>;
}

/** admin => full. mod => cần permKey. */
export function canAccess(u: UserInfo, permKey: string): boolean {
    if (isAdmin(u)) return true;
    if (u?.role === "mod") return !!normalizePerms(u?.permissions)[permKey];
    return false;
}

/** Lấy permKey từ path (segment đầu). */
export function pathToPerm(path: string): string | undefined {
    const seg = path.replace(/^\/+/, "").split("/")[0];
    return PATH_PERM[seg];
}

/** admin => full. mod => path phải map ra permKey được bật. Path không map => chỉ admin. */
export function canAccessPath(u: UserInfo, path: string): boolean {
    if (isAdmin(u)) return true;
    const perm = pathToPerm(path);
    return perm ? canAccess(u, perm) : false;
}

/** Route đầu tiên mod được vào (để redirect sau login / khi vào path bị cấm). */
export function firstAllowedPath(u: UserInfo): string {
    const order = ["comment", "referral", "logs"]; // ưu tiên các mục CTV hay dùng
    const byPerm = (perm: string) =>
        Object.keys(PATH_PERM).find((seg) => PATH_PERM[seg] === perm);
    for (const perm of order) {
        if (canAccess(u, perm)) return `/${byPerm(perm)}`;
    }
    // fallback: bất kỳ mục nào được phép
    for (const seg of Object.keys(PATH_PERM)) {
        if (canAccess(u, PATH_PERM[seg])) return `/${seg}`;
    }
    return "/login";
}
